import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { href: "/#services", label: "Services" },
  { href: "/#work", label: "Work" },
  { href: "/portfolio", label: "Portfolio" },
  { href: "/#process", label: "Process" },
  { href: "/#contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const renderLink = (l, mobile = false) => {
    const base = mobile
      ? "font-mono-accent text-xs uppercase tracking-[0.25em] text-white/70"
      : "font-mono-accent text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white transition-colors";
    const testid = `${mobile ? "nav-mobile-link-" : "nav-link-"}${l.label.toLowerCase()}`;
    const onClick = mobile ? () => setOpen(false) : undefined;
    // For hash links on the home page, plain anchor scrolls smoothly.
    // For hash links from other pages, anchor navigates home + scrolls.
    // For /portfolio (no hash) we use Link to avoid a full reload.
    if (l.href.includes("#")) {
      return (
        <a key={l.href} href={l.href} onClick={onClick} className={base} data-testid={testid}>
          {l.label}
        </a>
      );
    }
    return (
      <Link key={l.href} to={l.href} onClick={onClick} className={base} data-testid={testid}>
        {l.label}
      </Link>
    );
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 md:h-28 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center -my-2"
        >
          <img
            src="/NaN-Logotype-05-300x169.png"
            alt="NaN Studio"
            width="300"
            height="169"
            className="h-16 md:h-20 w-auto"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => renderLink(l, false))}
        </nav>

        <a
          href="/#contact"
          data-testid="nav-cta-start"
          className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#29ABE2] text-black font-medium text-sm hover:bg-[#5cc2ec] transition-colors"
        >
          Start a project
          <span className="font-mono-accent">↗</span>
        </a>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-black/90 backdrop-blur-xl border-t border-white/[0.06]">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => renderLink(l, true))}
            <a
              href="/#contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#29ABE2] text-black font-medium text-sm"
            >
              Start a project ↗
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
